import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DoneIcon from '@mui/icons-material/Done';

import Draggable from 'react-draggable';

import PopupMenu from './../popupmenu';
import StyledPaper from './../styled/paper';
import TagEditor, { RandomIcon, RandomColor } from './tageditor';
import ConfirmationDialog from './../confirmationdlg';
import DataContext from '../../data/data-context.js';
import TagsService from '../../data/tags-service.js';
import Auth from '../../data/auth-service.js';
import CMCsDataService, { CmcContext } from '../../data/cmcs-service';

import useWindowSize from '../../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    root: {
        zIndex: theme.zIndex.modal,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    paper: {
        maxWidth: 340,
        width: 340,
        '& .MuiListItemIcon-root': {
            minWidth: 0,
        },
    },
    tagsList: {
        maxHeight: 350,
        paddingRight: 0,
        paddingTop: 0,
        flexWrap: 'wrap',
        overflowY: 'scroll',
    },
    listItem: {
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word'
    },
    listEditText: {
        maxWidth: 150,
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word'
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    saveNewButton: {
        position: 'absolute',
        right: theme.spacing(6),
        top: theme.spacing(1),
    },
    titlebar: {
        padding: 0,
    },
    newButton: {
        margin: theme.spacing(1),
    },
    saveButton: {
        margin: 0
    },
    iconButton: {
        margin: 4,
        '& .MuiIcon-root': {
            fontSize: 'inherit',
        }
    },
    popperOverride: {
        backgroundColor: theme.palette.background.selectionAlt,
        '&::hover': {
            backgroundColor: theme.palette.background.selectionAlt,
        },
    },
}));

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

function PaperComponent (props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <StyledPaper {...props} />
        </Draggable>
    );
}

const TagList = (props) => {
    const classes = useStyles();
    const { upsertTag, deleteTag } = useContext(DataContext);
    const { height } = useWindowSize();
    const [deleteTagConfrm, setDeleteTagConfrm] = useState();
    const [state, setState] = useState('list');
    const [current, setCurrent] = useState();
    const [newtags, setNewtags] = useState({});
    const tagPerms = Auth.permissions.get('Tags');
    const alertPerms = Auth.permissions.get('Alerts');

    // mc2 cmc selection
    const [selectCMC, setSelectCMC] = useState();
    const [cmcs, setCmcs] = useState([]);

    const actions = [
        {
            title: 'Tag selection',
            action: props.onStartTagging ? t => {
                setCurrent(t);
                setState('tag');
                props.onStartTagging();
            } : undefined
        },
        alertPerms.write && {
            title: 'Tag all in current filter',
            action: props.onTagFilter ? t => props.onTagFilter(t.name) : undefined
        },
        alertPerms.delete && {
            title: 'Untag all in current filter',
            action: props.onUntagFilter ? t => props.onUntagFilter(t.name) : undefined
        }
    ];

    // tiny popup to fit a tiny monitor FML (1438 x 674)
    const getHeight = useCallback(() => {
        return Math.min(height / 3, 450);
    }, [height]);

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    const handleSaveNewTag = newTag => {
        if (MC2CHECK && !newTag.cmc) {
            setSelectCMC(newTag);
            return;
        }
        setSelectCMC();

        upsertTag(newTag);
        const newval = Object.values(newtags).reduce((a, c) => a > c ? a : c, 0) + 1;
        setNewtags({ ...newtags, [newTag.name]: newval });
    };

    const handleOnClick = t => {
        props.onTagSelect && props.onTagSelect(t.name);
    };

    // update handlers
    const handleSelectColor = color => {
        setCurrent({ ...current, ...{ color } });
        upsertTag({ ...current, ...{ color } });
    };

    const handleSelectIcon = icon => {
        setCurrent({ ...current, ...{ icon } });
        upsertTag({ ...current, ...{ icon } });
    };

    // delete handlers
    const handleDeleteTag = () => {
        setDeleteTagConfrm(undefined);
        deleteTag(deleteTagConfrm);
    };

    useEffect(() => {
        if (!props.show) setNewtags({});
    }, [props.show]);

    useEffect(() => {
        if (MC2CHECK) {
            CMCsDataService.getNames().then(cmcs => setCmcs(Object.values(cmcs || {})));
        }
    }, []);

    // boundariesElement: props.boundariesElement || 'window'
    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            role={undefined} disablePortal>
            <PaperComponent className={classes.paper}
                style={ (state === 'tag' && current) || state === 'list'
                    ? { maxWidth: 340, width: 340 }
                    : state === 'edit' || state === 'new'
                        ? { maxWidth: 255, width: 255 } : {} }>
                <div
                    onKeyDown={handleListKeyDown}
                    style={{ cursor: 'move' }}
                    id="draggable-dialog-title">
                    <ConfirmationDialog
                        onClose={() => setDeleteTagConfrm(undefined)}
                        onOK={handleDeleteTag}
                        open={deleteTagConfrm !== undefined}
                        message={`Delete tag ${(deleteTagConfrm || {}).name}?`}/>
                    {{
                        list: <Fade in={state === 'list'}>
                            <TagListList
                                onNew={!tagPerms.write ? undefined : () => setState('new')}
                                onEdit={!tagPerms.write ? undefined : t => { setCurrent(t); setState('edit') }}
                                onClick={handleOnClick}
                                onDelete={!tagPerms.delete ? undefined : t => setDeleteTagConfrm(t)}
                                onClose={props.onClickAway}
                                actions={actions.filter(a => a.action)}
                                newtags={newtags}
                                height={getHeight()}/>
                        </Fade>,
                        edit:
                            current && <TagListEdit
                                tag={current}
                                onColor={handleSelectColor}
                                onIcon={handleSelectIcon}
                                onClose={() => setState('list')}
                                height={getHeight()}/>,
                        tag:
                            current && <TagListTagging
                                tag={current}
                                onApply={() => { props.onFinishTagging(current.name); setState('list') }}
                                onClose={() => { props.onFinishTagging(); setState('list') }}/>,
                        new:
                            <TagListNew
                                onClose={() => setState('list')}
                                onSave={t => { handleSaveNewTag(t); setState('list') }}
                                height={getHeight()}/>,
                    }[state]}
                </div>
            </PaperComponent>
            { selectCMC && <SelectCMC
                tag={selectCMC}
                cmcs={cmcs}
                onClose={() => setSelectCMC()}
                onOK={handleSaveNewTag}/>
            }
        </Popper>
    );
};

TagList.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    onStartTagging: PropTypes.func,
    onFinishTagging: PropTypes.func,
    onTagSelect: PropTypes.func,
    onUntagFilter: PropTypes.func,
    onTagFilter: PropTypes.func,
};

const TagSimpleList = (props) => {
    const classes = useStyles();
    const { height } = useWindowSize();
    const { upsertTag, deleteTag } = useContext(DataContext);
    const [deleteTagConfrm, setDeleteTagConfrm] = useState();
    const [state, setState] = useState('list');
    const [current, setCurrent] = useState();
    const [newtags, setNewtags] = useState({});
    const tagPerms = Auth.permissions.get('Tags');

    // mc2 cmc selection
    const [selectCMC, setSelectCMC] = useState();
    const [cmcs, setCmcs] = useState([]);

    function handleListKeyDown (event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            props.onClickAway();
        }
    }

    // tiny popup to fit a tiny monitor FML (1438 x 674)
    const getHeight = useCallback(() => {
        return Math.min(height / 4, 450);
    }, [height]);

    const handleSaveNewTag = newTag => {
        if (MC2CHECK && !newTag.cmc) {
            setSelectCMC(newTag);
            return;
        }
        setSelectCMC();

        upsertTag(newTag);
        const newval = Object.values(newtags).reduce((a, c) => a > c ? a : c, 0) + 1;
        setNewtags({ ...newtags, [newTag.name]: newval });
    };

    // update handlers
    const handleSelectColor = color => {
        setCurrent({ ...current, ...{ color } });
        upsertTag({ ...current, ...{ color } });
    };

    const handleSelectIcon = icon => {
        setCurrent({ ...current, ...{ icon } });
        upsertTag({ ...current, ...{ icon } });
    };

    // delete handlers
    const handleDeleteTag = () => {
        setDeleteTagConfrm(undefined);
        deleteTag(deleteTagConfrm);
    };

    const handleOnClick = t => {
        props.onSelect(t);
        props.onClickAway();
    };

    useEffect(() => {
        if (!props.show) setNewtags({});
    }, [props.show]);

    useEffect(() => {
        if (MC2CHECK) {
            CMCsDataService.getNames().then(cmcs => setCmcs(Object.values(cmcs || {})));
        }
    }, []);

    // boundariesElement: props.boundariesElement || 'window'
    return (
        <Popper
            className={classes.root}
            open={props.show}
            anchorEl={props.anchorEl}
            placement={props.placement || 'bottom-start'}
            role={undefined} disablePortal>
            <StyledPaper className={classes.paper}
                style={ (state === 'tag' && current) || state === 'list'
                    ? { maxWidth: 340, width: 340 }
                    : state === 'edit' || state === 'new'
                        ? { maxWidth: 255, width: 255 } : {} }
                modifiers={{
                    flip: { enabled: false },
                    preventOverflow: { enabled: true },
                }}>
                <div onKeyDown={handleListKeyDown}>
                    <ConfirmationDialog
                        onClose={() => setDeleteTagConfrm(undefined)}
                        onOK={handleDeleteTag}
                        open={deleteTagConfrm !== undefined}
                        message={`Delete tag ${(deleteTagConfrm || {}).name}?`}/>
                    {{
                        list: <Fade in={state === 'list'}>
                            <TagListList
                                onNew={!tagPerms.write ? undefined : () => setState('new')}
                                onEdit={!tagPerms.write ? undefined : t => { setCurrent(t); setState('edit') }}
                                onClick={handleOnClick}
                                onDelete={!tagPerms.delete ? undefined : t => setDeleteTagConfrm(t)}
                                onClose={props.onClickAway}
                                newtags={newtags}
                                height={getHeight()}/>
                        </Fade>,
                        edit:
                                current && <TagListEdit
                                    tag={current}
                                    onColor={handleSelectColor}
                                    onIcon={handleSelectIcon}
                                    onClose={() => setState('list')}
                                    height={getHeight()}/>,
                        new:
                                <TagListNew
                                    onClose={() => setState('list')}
                                    onSave={t => { handleSaveNewTag(t); setState('list') }}
                                    height={getHeight()}/>,
                    }[state]}
                </div>
            </StyledPaper>
            { selectCMC && <SelectCMC
                tag={selectCMC}
                cmcs={cmcs}
                onClose={() => setSelectCMC()}
                onOK={handleSaveNewTag}/>
            }
        </Popper>
    );
};

TagSimpleList.propTypes = {
    show: PropTypes.bool.isRequired,
    placement: PropTypes.string,
    anchorEl: PropTypes.object,
    onClickAway: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};

const TagListList = React.forwardRef((props, ref) => {
    const classes = useStyles();
    const { tags } = useContext(DataContext);
    const [tagKeys, setTagKeys] = useState([]);
    const [search, setSearch] = useState('');

    const handleSearchChange = event => {
        setSearch(event.target.value || '');
    };

    // incoming
    useEffect(() => {
        setTagKeys(Object.keys(tags || {})
            .sort((a, b) => {
                // bubble new tags to the top in the order they were added in
                if (props.newtags[a] && !props.newtags[b]) return -1;
                if (props.newtags[b] && !props.newtags[a]) return 1;
                if (props.newtags[a] && props.newtags[b]) return props.newtags[b] - props.newtags[a];

                // the rest are sorted normally
                return (a.toLowerCase() < b.toLowerCase()) ? -1 : 1;
            })
            .map(k => tags[k]));
    }, [props.newtags, tags]);

    return (
        <div ref={ref}>
            <DialogTitle className={classes.titlebar}>
                <React.Fragment>
                    <IconButton
                        className={classes.iconButton}
                        disabled={!search}
                        aria-label="search tags"
                        size="large">
                        <SearchIcon />
                    </IconButton>
                    <InputBase
                        className={classes.input}
                        onChange={handleSearchChange}
                        value={search}
                        placeholder={'Search tags'}
                        inputProps={{ 'aria-label': 'Search tags', spellCheck: false }}/>
                </React.Fragment>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={props.onClose}
                    size="large">
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </DialogTitle>
            {props.onNew && <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.newButton}
                onClick={props.onNew}
                startIcon={<AddIcon />}> New Tag
            </Button>}
            <Box>
                <List className={classes.tagsList} style={{ height: props.height }}>
                    {tagKeys
                        .filter(tag => search.length < 0 || (tag.name || '').includes(search))
                        .map((tag, i) =>
                            <React.Fragment key={tag.name}>
                                <ListItem
                                    size="small"
                                    className={classes.listItem}>
                                    <ListItemIcon
                                        onClick={props.onEdit ? () => { props.onEdit(tag) } : undefined }>
                                        <IconButton
                                            key={tag.name + i}
                                            style={{ color: tag.color }}
                                            className={classes.iconButton}
                                            size="small">
                                            <Icon fontSize="inherit">{tag.icon}</Icon>
                                        </IconButton>
                                    </ListItemIcon>
                                    <ListItemText
                                        onClick={() => props.onClick(tag)}
                                        primary={tag.name} />
                                    {props.actions && props.actions.length > 0 &&
                                        <PopupMenu
                                            className={classes.root}
                                            items={props.actions}
                                            itemkey={'title'}
                                            onSelect={sel => sel.action(tag)}
                                            icon={<MenuIcon/>}
                                            size='small'
                                            placement={'bottom-end'}/>}
                                    {props.onDelete && <ListItemSecondaryAction>
                                        <IconButton
                                            onClick={() => props.onDelete(tag)}
                                            size="small"
                                            edge="end"
                                            aria-label="delete"
                                            className={classes.iconButton}>
                                            <DeleteIcon fontSize="inherit"/>
                                        </IconButton>
                                    </ListItemSecondaryAction>}
                                </ListItem>
                            </React.Fragment>
                        )}
                </List>
            </Box>
        </div>
    );
});
TagListList.displayName = 'TagListList';
TagListList.propTypes = {
    onNew: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    actions: PropTypes.array,
    newtags: PropTypes.object.isRequired,
    height: PropTypes.number.isRequired,
};

const TagListEdit = (props) => {
    const classes = useStyles();

    return props.tag ? <Box>
        <ListItem
            className={classes.listItem}
            size="small">
            <ListItemIcon>
                <IconButton
                    style={{ color: props.tag.color }}
                    className={classes.iconButton}
                    size="small">
                    <Icon fontSize="inherit">{props.tag.icon}</Icon>
                </IconButton>
            </ListItemIcon>
            <ListItemText primary={props.tag.name} className={classes.listEditText}/>
            <React.Fragment>
                <ListItemSecondaryAction
                    onClick={props.onClose}>
                    <IconButton edge="end" className={classes.saveButton} aria-label="save" size="large">
                        <DoneIcon fontSize="small"/>
                    </IconButton>
                </ListItemSecondaryAction>
            </React.Fragment>
        </ListItem>
        <TagEditor
            color={props.tag.color}
            icon={props.tag.icon}
            onSelectColor={props.onColor}
            onSelectIcon={props.onIcon}
            height={props.height}/>
    </Box> : '';
};
TagListEdit.propTypes = {
    tag: PropTypes.shape({
        color: PropTypes.string,
        icon: PropTypes.string,
        name: PropTypes.string.isRequired
    }).isRequired,
    onColor: PropTypes.func.isRequired,
    onIcon: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
};

const TagListNew = (props) => {
    const classes = useStyles();
    const [name, setName] = useState('');
    const [color, setColor] = useState(RandomColor());
    const [icon, setIcon] = useState(RandomIcon());

    const handleNameChange = e => {
        setName(e.target.value);
    };

    const isValid = () => TagsService.valid(name);

    return (
        <React.Fragment>
            <form noValidate autoComplete="off" onSubmit={() => props.onSave({ color, icon, name })}>
                <DialogTitle className={classes.titlebar}>
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={props.onClose}
                        size="large">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        aria-label="save"
                        className={classes.saveNewButton}
                        disabled={!isValid()}
                        onClick={() => props.onSave({ color, icon, name })}
                        size="large">
                        <SaveIcon fontSize="inherit"/>
                    </IconButton>
                </DialogTitle>
                <TextField
                    required
                    size="small"
                    variant="standard"
                    error={!isValid()}
                    style={{ marginLeft: 10, maxWidth: 130 }}
                    value={name}
                    onChange={handleNameChange}
                    label="Name"
                />
            </form>
            <TagEditor
                color={color}
                icon={icon}
                onSelectColor={setColor}
                onSelectIcon={setIcon}
                height={props.height}/>
        </React.Fragment>
    );
};
TagListNew.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
};

const TagListTagging = (props) => {
    const classes = useStyles();

    return props.tag ? <Box>
        <ListItem
            size="small">
            <ListItemIcon>
                <IconButton
                    style={{ color: props.tag.color }}
                    className={classes.iconButton}
                    size="small">
                    <Icon fontSize="inherit">{props.tag.icon}</Icon>
                </IconButton>
            </ListItemIcon>
            <ListItemText className={classes.listEditText} primary={props.tag.name} />
            <React.Fragment>
                <ListItemSecondaryAction>
                    <IconButton
                        onClick={props.onApply}
                        edge="end"
                        color="primary"
                        className={classes.saveButton}
                        aria-label="save"
                        size="large">
                        <DoneIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        onClick={props.onClose}
                        edge="end"
                        className={classes.saveButton}
                        aria-label="cancel"
                        size="large">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </ListItemSecondaryAction>
            </React.Fragment>
        </ListItem>
    </Box> : '';
};
TagListTagging.propTypes = {
    tag: PropTypes.object,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

const SelectCMC = ({ tag, cmcs, onClose, onOK }) => {
    const classes = useStyles();
    const { cmc, setCmc } = useContext(CmcContext);
    const [selection, setSelection] = useState((cmc || {}).name);

    const handleOnSelectCMC = useCallback(() => {
        onOK({ ...tag, cmc: selection });
    }, [cmc, setCmc, onOK]);

    return (
        <Dialog
            disableEscapeKeyDown
            maxWidth="xs"
            aria-labelledby="select CMC"
            open={(cmcs || []).length > 0}>
            <DialogTitle> Select CMC for <b>{`'${tag.name}'`}</b> </DialogTitle>
            <DialogContent dividers stylee={{ paddingBottom: 50 }}>
                Please select a CMC for the new tag
                <br/>
                <PopupMenu
                    tooltip="select CMC"
                    items={cmcs}
                    className={classes.popperOverride}
                    title={selection}
                    selection={selection}
                    onSelect={setSelection}
                    placement={'bottom-start'}
                    name="cmc selector"
                    zIndex={1500}/>
                <div style={{ minHeight: 45 }}></div>
            </DialogContent>
            <DialogActions>
                <Button
                    autoFocus
                    onClick={onClose}
                    color="primary"
                    aria-label="cancel">
                    Cancel
                </Button>
                <Button
                    enabled={cmc}
                    onClick={handleOnSelectCMC}
                    color="primary"
                    aria-label="Add Tag">
                    Add Tag
                </Button>
            </DialogActions>
        </Dialog>
    );
};
SelectCMC.propTypes = {
    tag: PropTypes.object.isRequired,
    cmcs: PropTypes.array.isRequired,
    onOK: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export {
    TagList as default, // for use in alert/mtdt list
    TagListList,
    TagListEdit,
    TagListTagging,
    TagListNew,
    TagSimpleList, // for use on a single alert/metdt and widgets
};
